import { Inject } from '@odinjs/odin';

import { Endpoint, ToastServiceProviderAccreditation } from '@philips-emr/hpms-provider-accreditation-commons';
import { HpmsCustomContentCollapseApi, HpmsCustomContentCollapseEvent, HPMSOnClickActionTitle, HPMSOnOpenCollapse } from '@philips-emr/hpms-provider-accreditation-components';
import { FetchAPI, FieldAPI, FieldVisibility, PanelAPI } from '@philips-emr/tws-components';
import Controller, { OnBlur, OnCancel, OnClose, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ClickEvent, MountEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';

import { NegotiationProceduresAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

interface Item {
  edit: string,
  ieValueCounterProposal: string,
  vlCpProcedure: number,
  vlCpDoctor: number,
  vlCpFilm: number,
  vlCpAnesthetist: number,
  vlCpAssistant: number,
  vlCpOperationalCost: number,
  nrSeqItemProposal: number,
  itemType: string,
}

const PROCEDURE = 'P';

@Controller({ fragment: FragmentNames.NEGOTIATION_PROCEDURES })
class NegotiationProceduresController extends BaseController {

  @Inject(Aliases.FETCH_PROCEDURES)
  private fetchProcedures: FetchAPI;
  @Inject(Aliases.PANEL_MODAL_COUNTER_OFFER)
  private panelModal: PanelAPI;
  @Inject(Aliases.FIELD_CP_PROCEDURE)
  private fieldProcedureValue: FieldAPI;
  @Inject(Aliases.FIELD_CP_OPERATIONAL_COST)
  private fieldOperatinalCost: FieldAPI;
  @Inject(Aliases.FIELD_CP_DOCTOR_VALUE_EDIT)
  private fieldDoctor: FieldAPI;
  @Inject(Aliases.FIELD_CP_FILM_VALUE_EDIT)
  private fieldFilm: FieldAPI;
  @Inject(Aliases.FIELD_CP_AUX_VALUES_EDIT)
  private fieldAuxiliar: FieldAPI;
  @Inject(Aliases.FIELD_CP_ANESTHETIST_EDIT)
  private fieldAnesthetist: FieldAPI;

  @OnBlur([
    Aliases.FIELD_CP_OPERATIONAL_COST, Aliases.FIELD_CP_DOCTOR_VALUE_EDIT,
    Aliases.FIELD_CP_FILM_VALUE_EDIT, Aliases.FIELD_CP_AUX_VALUES_EDIT,
    Aliases.FIELD_CP_ANESTHETIST_EDIT])
  onChangeValues(): void {
    const { vlCpOperationalCost } = this.fieldOperatinalCost.getData() as unknown as Item;
    const { vlCpAnesthetist } = this.fieldAnesthetist.getData() as unknown as Item;
    const { vlCpDoctor } = this.fieldDoctor.getData() as unknown as Item;
    const { vlCpFilm } = this.fieldFilm.getData() as unknown as Item;
    const { vlCpAssistant } = this.fieldAuxiliar.getData() as unknown as Item;

    const total = vlCpOperationalCost + vlCpAnesthetist + vlCpDoctor + vlCpFilm + vlCpAssistant;
    this.fieldProcedureValue.setValue(total);
  }

  @OnMount(Aliases.FETCH_PROCEDURES)
  onMountFetch(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqProposal, nrSeqNegotiation } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqNegotiation, nrSeqProposal, 'itemType': PROCEDURE });
    api.setParameters({ nrSeqNegotiation, nrSeqProposal, 'itemType': PROCEDURE });
    api.forceFetch();
  }

  @OnMount([
    Aliases.FIELD_PROCEDURE_VALUE,
    Aliases.FIELD_DOCTOR_VALUE,
    Aliases.FIELD_FILM_VALUE,
    Aliases.FIELD_OPERATIONAL_COST])
  onMountFieldsValue(event: MountEvent): void {
    const api = event.target as FieldAPI;
    const { ieValueCounterProposal } = api.getData() as unknown as Item;
    if (ieValueCounterProposal == 'S') {
      api.setVisibility(FieldVisibility.EDITING);
    }
  }

  @OnSubmit(Aliases.FORM_COUNTER_OFFER)
  onSubmitProcedure(event: SubmitEvent): void {
    const body = { ...event.record, 'itemType': PROCEDURE };
    const { nrSeqProposal, nrSeqNegotiation } = this.historyService.getCurrentRouteParams();

    HttpService.post(Endpoint.POST.EDIT_NEGOTIATION_PROPOSAL_ITEM, { nrSeqProposal, nrSeqNegotiation }, { body })
      .then(() => {
        this.fetchProcedures.forceFetch();
        this.panelModal.setVisible(false);
      }).catch(() => {
        ToastServiceProviderAccreditation.error(MessageService.translate('pa.error-process-request'));
      });
  }

  @HPMSOnOpenCollapse([Aliases.PROPOSAL_COLAPSE, Aliases.COUNTER_PROPOSAL_COLAPSE])
  onOpenRenderContent(event: HpmsCustomContentCollapseEvent<Item>): void {
    if (!event || !event.data) {
      return;
    }
    const api = event.target as HpmsCustomContentCollapseApi;
    const { data, onOpenRender } = event;
    const { edit, nrSeqItemProposal } = data;
    const { nrSeqProposal, nrSeqNegotiation } = this.historyService.getCurrentRouteParams();
    api.setHasTitleAction(edit == 'S');
    onOpenRender({ parameters: { nrSeqProposal, nrSeqNegotiation, type: data.itemType, nrSeqItemProposal } });
  }

  @OnMount(Aliases.FETCH_COUNT_OFFER)
  onMountFetchCounterOffer(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqProposal, nrSeqNegotiation } = this.historyService.getCurrentRouteParams();
    const { nrSeqItemProposal } = this.context.getBulkData() as unknown as Item;
    api.setParameters({ nrSeqNegotiation, nrSeqProposal, 'itemType': PROCEDURE, nrSeqItemProposal });
    api.forceFetch();
  }

  @HPMSOnClickActionTitle(Aliases.COUNTER_PROPOSAL_COLAPSE)
  onClickActionTitle(event: ClickEvent): void {
    const nrSeqItemProposal = event.value;
    this.context.set({ nrSeqItemProposal });
    this.panelModal.setVisible(true);
  }

  @OnClose(Aliases.MODAL_COUNTER_PROPOSAL)
  onCloseModal(): void {
    this.panelModal.setVisible(false);
  }

  @OnCancel(Aliases.FORM_COUNTER_OFFER)
  onCancelCounterOffer(): void {
    this.panelModal.setVisible(false);
  }

}

export {
  NegotiationProceduresController,
  Item,
};
